import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  AreaChart, Area,
  XAxis, YAxis,
  CartesianGrid, Tooltip,
  ResponsiveContainer,
} from "recharts";
import apiClient from "../api/client";
import "./AssetDetailPage.css";

// ── Time range options ────────────────────────────────────────────────────────
const TIME_RANGES = [
  { label: "שבוע",     days: 7 },
  { label: "חודש",     days: 30 },
  { label: "3 חודשים", days: 90 },
  { label: "שנה",      days: 365 },
  { label: "הכל",      days: null },
];

// ── Formatters ────────────────────────────────────────────────────────────────
function fmt(n, decimals = 2) {
  if (n == null || isNaN(n)) return "—";
  return Number(n).toLocaleString("he-IL", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

function fmtShortDate(d) {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit" });
}

// ── Change chip ───────────────────────────────────────────────────────────────
function ChangeChip({ pct }) {
  if (pct == null) return null;
  const up = pct >= 0;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 10px", borderRadius: 20, fontSize: 13, fontWeight: 500,
      background: up ? "rgba(52,199,89,0.1)" : "rgba(255,59,48,0.1)",
      color: up ? "#1e7d34" : "#c0392b",
    }}>
      {up ? "▲" : "▼"} {Math.abs(pct).toFixed(2)}%
    </span>
  );
}

// ── Custom tooltip ────────────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label, valueSuffix }) {
  if (!active || !payload?.length) return null;
  const val = payload[0]?.value;
  return (
    <div style={{
      background: "var(--bg)",
      border: "1px solid var(--border-light)",
      borderRadius: "var(--radius-sm)",
      padding: "8px 12px",
      fontSize: 13,
      boxShadow: "var(--shadow)",
    }}>
      <div style={{ color: "var(--text-secondary)", marginBottom: 2 }}>{label}</div>
      <div style={{ fontWeight: 600, color: "var(--text)" }}>
        {fmt(val, 4)}{valueSuffix}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function AssetDetailPage() {
  // assetType: "base-rate" | "fx" | "stock"
  // id:  mongo id for base-rate; "USD-ILS" for fx; "DLEKG.TA" for stock
  const { assetType, id } = useParams();
  const navigate = useNavigate();

  const [current, setCurrent]   = useState(null);
  const [history, setHistory]   = useState([]);
  const [holding, setHolding]   = useState(null); // stock only
  const [pledges, setPledges]   = useState([]);   // stock only — active pledges against this asset
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [timeRange, setTimeRange] = useState(TIME_RANGES[2]); // default 3M

  // ── Fetch ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!assetType || !id) return;
    setLoading(true);
    setError(null);
    setCurrent(null);
    setHistory([]);
    setHolding(null);
    setPledges([]);

    const load = async () => {
      if (assetType === "base-rate") {
        const [ratesRes, histRes] = await Promise.all([
          apiClient.get("/baseRates"),
          apiClient.get(`/baseRates/${id}/history`),
        ]);
        const rate = (ratesRes.data || []).find((r) => r._id === id);
        setCurrent(rate || null);
        setHistory(histRes.data || []);

      } else if (assetType === "fx") {
        const pair = id.replace(/-/g, "/"); // USD-ILS → USD/ILS
        const [fxRes, histRes] = await Promise.all([
          apiClient.get("/api/rates/fx"),
          apiClient.get(`/api/rates/fx/${id}/history`),
        ]);
        const curr = (fxRes.data || {})[pair] || null;
        setCurrent(curr ? { ...curr, pair } : null);
        setHistory(histRes.data || []);

      } else if (assetType === "stock") {
        const symbol = id.toUpperCase();
        const [snapRes, histRes, holdingsRes] = await Promise.allSettled([
          apiClient.get(`/api/rates/stock/${symbol}`),
          apiClient.get(`/api/rates/stock/${symbol}/history`),
          apiClient.get("/asset-holdings"),
        ]);
        if (snapRes.status === "fulfilled") setCurrent(snapRes.value.data || null);
        if (histRes.status === "fulfilled") setHistory(histRes.value.data || []);
        if (holdingsRes.status === "fulfilled") {
          const symbol = id.toUpperCase();
          const matches = (holdingsRes.value.data || []).filter(
            (h) => h.symbol === symbol && h.assetType === "STOCK"
          );
          if (!matches.length) {
            setHolding(null);
          } else {
            // The API returns manual unpledged lots and calculated pledged lots
            // separately. Show their combined ownership on the stock detail page.
            const first = matches[0];
            setHolding({
              ...first,
              assetName: first.assetName || symbol,
              totalUnits: matches.reduce((sum, h) => sum + (Number(h.totalUnits) || 0), 0),
              pledgedUnits: matches.reduce((sum, h) => sum + (Number(h.pledgedUnits) || 0), 0),
              freeUnits: matches.reduce((sum, h) => sum + (Number(h.freeUnits) || 0), 0),
              restrictedUnits: matches.reduce((sum, h) => sum + (Number(h.restrictedUnits ?? h.minimumFreeRequired) || 0), 0),
              usableUnits: matches.reduce((sum, h) => sum + (Number(h.usableUnits) || 0), 0),
            });
            try {
              // The same symbol can be held under multiple AssetHolding docs
              // (e.g. different owningCompany — no unique constraint on symbol),
              // so pledges must be fetched per match and aggregated, not just
              // read off `first`. Dedupe _ids so a repeated holding doc doesn't
              // trigger a duplicate fetch.
              const assetIds = [...new Set(matches.map((h) => h._id))];
              const pledgeResponses = await Promise.all(
                assetIds.map((assetId) => apiClient.get(`/pledges?assetId=${assetId}&status=ACTIVE`))
              );
              setPledges(pledgeResponses.flatMap((r) => r.data || []));
            } catch {
              setPledges([]);
            }
          }
        }
      }
    };

    load()
      .catch((err) => setError(err?.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, [assetType, id]);

  // ── Current displayed value ───────────────────────────────────────────────
  const currentValue = useMemo(() => {
    if (!current) return null;
    if (assetType === "base-rate") return current.value;
    if (assetType === "fx")        return current.rate;
    if (assetType === "stock")     return current.price;
    return null;
  }, [current, assetType]);

  // ── Day change (compare last 2 history points) ────────────────────────────
  const dayChangePct = useMemo(() => {
    if (assetType === "stock") return current?.changePct ?? null;
    if (history.length < 2) return null;
    const getRaw = (h) => h.value ?? h.close ?? h.rate;
    const curr = getRaw(history[0]);
    const prev = getRaw(history[1]);
    if (curr == null || prev == null || prev === 0) return null;
    return ((curr - prev) / prev) * 100;
  }, [history, assetType, current]);

  // ── Filter history by selected time range ─────────────────────────────────
  const filteredHistory = useMemo(() => {
    if (!timeRange.days) return history;
    const cutoff = Date.now() - timeRange.days * 24 * 60 * 60 * 1000;
    return history.filter((h) => {
      const d = new Date(h.ts || h.date || h.createdAt);
      return d.getTime() >= cutoff;
    });
  }, [history, timeRange]);

  // ── Chart data (oldest → newest for left-to-right display) ────────────────
  const chartData = useMemo(() => {
    return [...filteredHistory].reverse().map((h) => {
      const rawVal = h.value ?? h.close ?? h.rate;
      const value = rawVal;
      const dateStr = h.ts || h.date
        ? fmtShortDate(h.ts || h.date)
        : fmtShortDate(h.createdAt);
      return { date: dateStr, value };
    });
  }, [filteredHistory]);

  // ── Page title ────────────────────────────────────────────────────────────
  const title = useMemo(() => {
    if (assetType === "base-rate") return current?.name || id;
    if (assetType === "fx")        return id.replace(/-/g, "/");
    if (assetType === "stock")     return id.toUpperCase();
    return id;
  }, [current, assetType, id]);

  const subtitle = useMemo(() => {
    if (assetType === "base-rate") return "ריבית בסיס";
    if (assetType === "fx")        return "שער חליפין";
    if (assetType === "stock")     return holding?.assetName || "מניה";
    return "";
  }, [assetType, holding]);

  const valueSuffix = assetType === "base-rate" ? "%" : assetType === "stock" ? ` ${current?.currency || "ILS"}` : " ₪";
  const valueDecimals = assetType === "base-rate" ? 2 : 4;

  // ── Stock: portfolio KPIs ─────────────────────────────────────────────────
  const stockKPIs = useMemo(() => {
    if (assetType !== "stock" || !holding || !currentValue) return null;
    const totalValue = holding.totalUnits * (current.priceILS ?? 0);
    const mktCap = current?.marketCap;
    return {
      units:      holding.totalUnits,
      totalValue,
      mktCap,
      ownershipPct: mktCap ? (totalValue / mktCap) * 100 : null,
      pledgedUnits: holding.pledgedUnits || 0,
      restrictedUnits: holding.restrictedUnits || 0,
      usableUnits: holding.usableUnits || 0,
    };
  }, [assetType, holding, currentValue, current]);

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) return <div className="adp-loading">טוען...</div>;
  if (error)   return <div className="adp-error">שגיאה: {error}</div>;

  return (
    <div className="adp-page">

      {/* Header */}
      <div className="adp-header">
        <button className="btn btn-ghost" onClick={() => navigate(-1)} style={{ flexShrink: 0 }}>
          ← חזרה
        </button>
        <div className="adp-title-group">
          <h1 className="adp-title">{title}</h1>
          <p className="adp-subtitle">{subtitle}</p>
        </div>
      </div>

      {/* KPI row */}
      <div className="adp-kpi-row">
        {/* Main value */}
        <div className="adp-kpi-main">
          <div className="adp-kpi-value">
            {assetType === "base-rate"
              ? `${fmt(currentValue, 2)}%`
              : assetType === "stock"
                ? new Intl.NumberFormat("he-IL", { style: "currency", currency: current?.currency || "ILS", minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(currentValue)
                : <>{fmt(currentValue, valueDecimals)}<span className="adp-kpi-unit">₪</span></>
            }
          </div>
          <div className="adp-kpi-meta">
            <ChangeChip pct={dayChangePct} />
            <span className="adp-kpi-updated">
              {current?.asOf || current?.lastUpdated || current?.createdAt
                ? `עודכן ${fmtDate(current.asOf || current.lastUpdated || current.createdAt)}`
                : ""}
            </span>
            {current?.stale && <span className="adp-kpi-updated">נתון חי מתעכב</span>}
          </div>
        </div>

        {/* Stock extra KPIs */}
        {stockKPIs && (
          <>
            <div className="adp-kpi-card">
              <div className="adp-kpi-card-label">מניות בבעלות</div>
              <div className="adp-kpi-card-value">{fmt(stockKPIs.units, 0)}</div>
            </div>
            <div className="adp-kpi-card">
              <div className="adp-kpi-card-label">שווי אחזקה</div>
              <div className="adp-kpi-card-value">₪{fmt(stockKPIs.totalValue, 0)}</div>
            </div>
            <div className="adp-kpi-card">
              <div className="adp-kpi-card-label">מניות מוגבלות</div>
              <div className="adp-kpi-card-value">{fmt(stockKPIs.restrictedUnits, 0)}</div>
            </div>
            <div className="adp-kpi-card">
              <div className="adp-kpi-card-label">זמין לשימוש</div>
              <div className="adp-kpi-card-value">{fmt(stockKPIs.usableUnits, 0)}</div>
            </div>
            {stockKPIs.mktCap && (
              <div className="adp-kpi-card">
                <div className="adp-kpi-card-label">שווי שוק</div>
                <div className="adp-kpi-card-value">
                  {stockKPIs.mktCap >= 1e9
                    ? `${(stockKPIs.mktCap / 1e9).toFixed(2)}B`
                    : `${(stockKPIs.mktCap / 1e6).toFixed(0)}M`}
                </div>
              </div>
            )}
            {stockKPIs.ownershipPct != null && (
              <div className="adp-kpi-card">
                <div className="adp-kpi-card-label">אחוז בעלות</div>
                <div className="adp-kpi-card-value">{fmt(stockKPIs.ownershipPct, 4)}%</div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Chart */}
      <div className="adp-chart-section">
        <div className="adp-time-pills">
          {TIME_RANGES.map((r) => (
            <button
              key={r.label}
              className={`adp-time-pill${timeRange === r ? " active" : ""}`}
              onClick={() => setTimeRange(r)}
            >
              {r.label}
            </button>
          ))}
        </div>

        {chartData.length < 2 ? (
          <div className="adp-empty">
            {chartData.length === 0 ? "אין נתונים לתקופה זו" : "נדרשים לפחות 2 נקודות נתונים"}
          </div>
        ) : (
          <div className="adp-chart-wrap">
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={chartData} margin={{ top: 8, right: 4, left: 4, bottom: 0 }}>
                <defs>
                  <linearGradient id="adpGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#0071e3" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#0071e3" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: "var(--text-secondary)" }}
                  tickLine={false}
                  axisLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "var(--text-secondary)" }}
                  tickLine={false}
                  axisLine={false}
                  domain={["auto", "auto"]}
                  width={55}
                  tickFormatter={(v) =>
                    assetType === "base-rate" ? `${v}%` : `${fmt(v, 2)}`
                  }
                />
                <Tooltip content={<ChartTooltip valueSuffix={valueSuffix} />} />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#0071e3"
                  strokeWidth={2}
                  fill="url(#adpGrad)"
                  dot={false}
                  activeDot={{ r: 4, fill: "#0071e3", strokeWidth: 0 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* History table */}
      <div className="adp-history-section">
        <h2 className="adp-section-title">היסטוריה</h2>
        {history.length === 0 ? (
          <div style={{ color: "var(--text-secondary)", fontSize: 14 }}>אין היסטוריה זמינה</div>
        ) : (
          <div className="adp-table-wrap">
            <table className="adp-table">
              <thead>
                <tr>
                  <th>תאריך</th>
                  <th>ערך</th>
                  {assetType === "base-rate" && <th>מקור</th>}
                  {assetType === "stock" && <th>פתיחה</th>}
                  {assetType === "stock" && <th>גבוה</th>}
                  {assetType === "stock" && <th>נמוך</th>}
                  {assetType === "stock" && <th>נפח</th>}
                  {assetType === "fx" && <th>מקור עדכון</th>}
                </tr>
              </thead>
              <tbody>
                {history.slice(0, 200).map((h, i) => {
                  const rawVal = h.value ?? h.close ?? h.rate;
                  const displayVal = rawVal;
                  const dateStr = fmtDate(h.ts || h.date || h.createdAt);
                  return (
                    <tr key={h._id || i}>
                      <td>{dateStr}</td>
                      <td style={{ fontWeight: 500 }}>
                        {fmt(displayVal, valueDecimals)}{assetType === "base-rate" ? "%" : ""}
                      </td>
                      {assetType === "base-rate" && (
                        <td className="adp-source">{h.source || "—"}</td>
                      )}
                      {assetType === "stock" && (
                        <>
                          <td>{fmt(h.open, 4)}</td>
                          <td>{fmt(h.high, 4)}</td>
                          <td>{fmt(h.low, 4)}</td>
                          <td>{h.volume != null ? Number(h.volume).toLocaleString("he-IL") : "—"}</td>
                        </>
                      )}
                      {assetType === "fx" && (
                        <td className="adp-source">{h.raw?.lastUpdate || "—"}</td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pledges panel — where this asset is pledged (stock only) */}
      {assetType === "stock" && holding && (
        <div className="adp-pledges-section">
          <h2 className="adp-section-title">משועבד היכן</h2>
          {pledges.length === 0 ? (
            <div style={{ color: "var(--text-secondary)", fontSize: 14 }}>הנכס אינו משועבד</div>
          ) : (
            <div className="adp-table-wrap">
              <table className="adp-table">
                <thead>
                  <tr>
                    <th>מסגרת</th>
                    <th>נושה משעבד</th>
                    <th>יחידות משועבדות</th>
                    <th>החל מ-</th>
                  </tr>
                </thead>
                <tbody>
                  {pledges.map((p) => {
                    // basketId is populated: { _id, name, isSingleton }. Singleton
                    // baskets are auto-created wrappers for standalone loans (hidden
                    // in all UI) — show a generic label instead of the internal name.
                    const basket = p.basketId;
                    const basketLabel = basket ? (basket.isSingleton ? "הלוואה עצמאית" : (basket.name || "—")) : "—";
                    return (
                      <tr key={p._id}>
                        <td>{basketLabel}</td>
                        <td>{p.lienHolder || "—"}</td>
                        <td>{p.pledgedUnits != null ? fmt(p.pledgedUnits, 0) : "נכס בשלמותו"}</td>
                        <td>{fmtDate(p.createdAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
